import { apiClient } from "./apiClient";

export interface Study {
  id: string;
  name: string;
  description: string;
  study_type: string;
  configuration: any;
  status: string;
  created_at: string;
  updated_at: string;
  template_id?: string;
  template_key?: string;
  template_info?: StudyTemplate;
  latest_execution?: StudyExecution;
}

export interface StudyExecution {
  id: string;
  study: string;
  input_snapshot: any;
  output_data: any;
  metrics: any;
  status: "queued" | "running" | "completed" | "failed" | string;
  error?: string;
  executed_at: string;
  completed_at?: string | null;
}

export interface DemandForecastabilityBucket {
  key: "smooth" | "intermittent" | "erratic" | "lumpy";
  label: string;
  description: string;
  count: number;
  share_pct: number;
  // null on the lightweight /profile path — no backtest has run yet,
  // so the FE renders "—" rather than fabricating a textbook value.
  accuracy_pct: number | null;
  model: string | null;
  color: "green" | "blue" | "yellow" | "red";
}

export interface DemandForecastability {
  total: number;
  buckets: DemandForecastabilityBucket[];
  /** Resolved schema columns. Useful for surfacing "we treated <X> as
   *  the SKU column" / warning when no SKU column was identified
   *  (silent single-series fallback). Optional because older BE builds
   *  don't emit it. */
  inferred_schema?: {
    sku: string | null;
    date: string | null;
    value: string | null;
  };
  /** Free-form warnings the BE wants the planner to see. Each note
   *  has a code (for grouping/i18n later) and a human message. */
  notes?: Array<{ code: string; severity: "info" | "warning" | "error"; message: string }>;
  /** Lightweight data-profile stats from forecast-svc /profile. Drives
   *  Step 3's Completeness / Missing / Outlier KPIs so Foundry-backed
   *  studies see real numbers instead of the legacy mock "97.4%". */
  dataProfile?: {
    n_rows?: number;
    n_skus?: number;
    period_range?: string[];
    pct_complete?: number;
    missing_count?: number;
    duplicate_count?: number;
    zero_count?: number;
    pct_zero?: number;
    negative_count?: number;
    outlier_count?: number;
    /** Distinct values per mapped key column (sku, region, …). */
    dim_cardinality?: Record<string, number>;
    detected_frequency?: string;
    detected_sku_column?: string | null;
    detected_date_column?: string;
    detected_value_column?: string;
    [key: string]: any;
  } | null;
}

export interface StudyTemplate {
  id: string;
  key: string;
  name: string;
  description: string;
  category: string;
  icon?: string;
  route: string;
  default_configuration: any;
  /**
   * False when the current tenant has explicitly disabled this study via
   * `TenantStudyConfig`. Defaults to true when no override row exists.
   */
  enabled_for_tenant?: boolean;
}

export const studyApi = {
  listTemplates: async () => {
    return await apiClient.get<StudyTemplate[]>("/templates/");
  },

  list: async () => {
    return await apiClient.get<Study[]>("/studies/");
  },

  get: async (id: string) => {
    return await apiClient.get<Study>(`/studies/${id}/`);
  },

  create: async (data: Partial<Study>) => {
    return await apiClient.post<Study>("/studies/", data);
  },

  update: async (id: string, data: Partial<Study>) => {
    return await apiClient.patch<Study>(`/studies/${id}/`, data);
  },

  delete: async (id: string) => {
    await apiClient.delete(`/studies/${id}/`);
  },

  /**
   * Bulk delete. BE refuses to delete studies whose latest execution is
   * queued/running and returns them in `skipped` so the caller can show a
   * partial-success toast.
   */
  bulkDelete: async (ids: string[]): Promise<BulkDeleteResponse> => {
    return await apiClient.post<BulkDeleteResponse>(`/studies/bulk_delete/`, { ids });
  },

  run: async (id: string) => {
    return await apiClient.post<StudyExecution>(`/studies/${id}/run/`);
  },

  identifyGaps: async (id: string) => {
    return await apiClient.post<any>(`/studies/${id}/identify_gaps/`);
  },

  /**
   * Lightweight per-SKU demand classification (Smooth / Intermittent / Erratic
   * / Lumpy). Runs against rows passed inline OR whatever's in
   * study.configuration. Inline rows are preferred — they let the FE
   * preview the chart before persisting MB of upload data to the study.
   */
  demandForecastability: async (id: string, rows?: Array<Record<string, any>>) => {
    if (rows && rows.length) {
      return await apiClient.post<DemandForecastability>(
        `/studies/${id}/demand_forecastability/`,
        { rows },
      );
    }
    return await apiClient.get<DemandForecastability>(
      `/studies/${id}/demand_forecastability/`,
    );
  },

  /**
   * Starter-template data for Demand Forecast Step 1. Returns a
   * slot→FoundryObject mapping with the canonical column_mapping per
   * slot so the FE can apply the result directly to
   * `/studies/<id>/input_slots/` without bouncing through the
   * auto-mapper.
   *
   * Auto-creates any missing template on the BE side, so a user who
   * previously deleted them still gets a working result.
   */
  dfStarterTemplates: async () => apiClient.post<{
    slots: Record<string, {
      object_id: string;
      object_name: string;
      object_type: string;
      column_mapping: Record<string, string>;
    }>;
    objects: Array<{
      id: string;
      name: string;
      slug: string;
      object_type: string;
      table_name: string;
      row_count: number | null;
    }>;
  }>(`/templates/df-starter/`, {}),

  /**
   * Tenant-admin only: enable or disable a study for the current tenant.
   * BE responds with 403 if the user isn't a tenant admin.
   */
  toggleTemplate: async (key: string, enabled: boolean) => {
    return await apiClient.post<{ key: string; enabled_for_tenant: boolean }>(
      `/templates/${key}/toggle/`,
      { enabled },
    );
  },

  /**
   * Canonical input schema for a study type. Powers the FE mapping UI:
   * each TemplateField becomes one row in the editor.
   */
  inputTemplate: async (id: string): Promise<InputTemplate> =>
    apiClient.get<InputTemplate>(`/studies/${id}/input_template/`),

  /**
   * Tenant-wide flat list of executions for the Jobs page.
   * Optional filters narrow by status / study_type / study.
   */
  listExecutions: async (params?: {
    status?: string;        // comma-separated: "queued,running"
    study_type?: string;
    study?: string;
    limit?: number;
  }): Promise<{ results: ExecutionRow[]; count: number }> => {
    const q = new URLSearchParams();
    if (params?.status)     q.set("status", params.status);
    if (params?.study_type) q.set("study_type", params.study_type);
    if (params?.study)      q.set("study", params.study);
    if (params?.limit)      q.set("limit", String(params.limit));
    const qs = q.toString();
    return apiClient.get(`/studies/executions/${qs ? `?${qs}` : ""}`);
  },

  /**
   * Auto-mapping proposal. Returns a per-canonical-field suggestion
   * with confidence scores. Optionally pass `rows` to preview a fresh
   * upload before persisting it to the study.
   */
  autoMap: async (id: string, rows?: Array<Record<string, any>>): Promise<AutoMapResponse> =>
    apiClient.post<AutoMapResponse>(`/studies/${id}/auto_map/`, rows ? { rows } : {}),

  /**
   * GET /studies/<id>/artifacts/ — fresh per-table CSV download URLs
   * (15-min TTL) plus the run-time audit metadata. Returns 404 when
   * no completed execution exists yet.
   */
  /**
   * Filtered, re-aggregated history + forecast for the results chart —
   * resolved server-side against the wide panel, so region / channel /
   * class / SKU filters apply to both lines and zoom re-rolls by period.
   * With `drill` (an ISO date) the response also lists the top series
   * behind that point.
   */
  series: async (id: string, params: SeriesParams = {}): Promise<SeriesResponse> => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== "" && v !== "all") q.set(k, String(v));
    }
    const qs = q.toString();
    return apiClient.get<SeriesResponse>(`/studies/${id}/series/${qs ? `?${qs}` : ""}`, { skipTenant: true });
  },

  artifacts: async (id: string): Promise<StudyArtifactsResponse> =>
    apiClient.get<StudyArtifactsResponse>(`/studies/${id}/artifacts/`),

  /**
   * GET /studies/<id>/transformed_panel/ — preview of the panel that
   * gets sent to forecast-svc, after rolling up to the planner-chosen
   * `forecastHierarchy` and joining any Foundry features. Used by the
   * results screen so a planner can see exactly what got forecast on.
   *
   * Computed on demand; cheap enough for a preview but not intended
   * as a bulk export. Use the artifacts CSVs for that.
   */
  transformedPanel: async (
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<TransformedPanelResponse> => {
    const q = new URLSearchParams();
    if (params?.limit != null)  q.set("limit",  String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return apiClient.get<TransformedPanelResponse>(
      `/studies/${id}/transformed_panel/${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * GET /studies/<id>/transformed_panel/download/
   * Builds the full panel server-side, uploads to GCS, returns a
   * short-lived signed URL the FE hands to an `<a download>`. Avoids
   * streaming millions of cells through the JSON path.
   */
  transformedPanelDownload: async (
    id: string,
  ): Promise<TransformedPanelDownloadResponse> =>
    apiClient.get<TransformedPanelDownloadResponse>(
      `/studies/${id}/transformed_panel/download/`,
    ),

  /**
   * GET /studies/<id>/run_progress/ — live heartbeat from forecast-svc
   * (current stage, SKUs evaluated, free-form message). Returns
   * `progress: null` once the execution is completed/failed/idle so
   * callers can stop polling. Cheap enough to call every 3s while a
   * run is in flight.
   */
  runProgress: async (id: string): Promise<RunProgressResponse> =>
    apiClient.get<RunProgressResponse>(`/studies/${id}/run_progress/`),
};

export interface BulkDeleteResponse {
  deleted: string[];
  skipped: Array<{ id: string; reason: string }>;
}

export interface RunProgressResponse {
  execution_id?: string;
  status: "idle" | "queued" | "running" | "completed" | "failed";
  registry_status?: string | null;
  error?: string;
  progress: {
    stage: string;
    current: number;
    total: number;
    message: string;
    updated_at: string;
  } | null;
}

export interface TransformedPanelResponse {
  rows: Array<Record<string, any>>;
  columns: string[];
  total: number;
  limit: number;
  offset: number;
  hierarchy: {
    fields: string[];           // canonical keys e.g. ["sku","region"]
    columns: string[];          // actual column names from the data
    composite_column: string | null;
  };
  schema: {
    date_column: string | null;
    value_column: string | null;
  };
  notes?: Array<{ code: string; severity: "info" | "warning" | "error"; message: string }>;
  error?: string;
  detail?: string;
}

export interface TransformedPanelDownloadResponse {
  download_url?: string;
  rows?: number;
  columns?: number;
  byte_size?: number;
  expires_in_seconds?: number;
  filename?: string;
  // Failure envelopes
  error?: string;
  exception?: string;
  detail?: string;
  stage?: string;
}


// ── Input-template + mapping types (mirror of prajna/templates.py) ───

export interface TemplateField {
  key: string;
  label: string;
  description: string;
  role: "dimension" | "time" | "measure" | "metadata" | "id" | string;
  dtype: "string" | "number" | "date" | "boolean" | "category" | string;
  required: boolean;
  aliases: string[];
  name_pattern?: string | null;
  enum_values?: string[] | null;
  default?: string | null;
}

export interface InputTemplate {
  study_type: string;
  title: string;
  description: string;
  fields: TemplateField[];
}

export interface MappingCandidate {
  column: string;
  score: number;
  name_score: number;
  dtype_score: number;
}

export interface MappingProposalEntry {
  column: string | null;
  confidence: number;
  auto_accepted: boolean;
  /** True when BE's LLM fallback overrode the heuristic pick. Set when
   *  the heuristic's top candidate scored below the fallback threshold
   *  (0.65) and Azure OpenAI suggested a different column. The FE can
   *  render a subtle "AI-suggested" badge so planners know to verify. */
  llm_assisted?: boolean;
  candidates: MappingCandidate[];
  field: {
    key: string;
    label: string;
    description: string;
    role: string;
    dtype: string;
    required: boolean;
  };
}

export interface MappingProposal {
  _meta: { columns: string[]; n_rows: number };
  [field_key: string]: MappingProposalEntry | { columns: string[]; n_rows: number };
}

export interface AutoMapResponse {
  template: InputTemplate;
  proposal: MappingProposal;
  saved_mapping: Record<string, string>;
}


// ── Artifact types ────────────────────────────────────────────────────


export interface StudyArtifact {
  name: string;
  content_type: string;
  size_bytes?: number | null;
  row_count?: number | null;
  download_url: string;
}

export interface SeriesParams {
  sku?: string;
  abc?: string;
  region?: string;
  channel?: string;
  category?: string;
  granularity?: "W" | "M" | "Q";
  drill?: string;
  top?: number;
}

export interface SeriesPoint { date: string; value: number }
export interface ForecastSeriesPoint extends SeriesPoint { lower: number; upper: number }

export interface SeriesDrilldown {
  date: string;
  source: "forecast" | "history" | null;
  total: number;
  series_in_period?: number;
  rows: Array<{ sku: string; value: number; share_pct: number; abc: string | null; model: string | null }>;
}

export interface SeriesResponse {
  execution_id: string | null;
  granularity: "W" | "M" | "Q";
  series_count: number;
  historical: SeriesPoint[];
  forecast: ForecastSeriesPoint[];
  /** Distinct values per mapped dimension, for the filter pickers. */
  options: Record<string, string[]>;
  filters: Record<string, string>;
  drilldown?: SeriesDrilldown;
}

export interface StudyArtifactsResponse {
  execution_id: string;
  completed_at: string | null;
  artifacts: StudyArtifact[];
  run_meta?: {
    started_at?: string;
    completed_at?: string;
    duration_ms?: number;
    build_git_sha?: string;
    statsforecast_version?: string;
    input_row_count?: number;
    input_sha256?: string | null;
    result_sku_count?: number;
    result_horizon_months?: number;
  };
  detail?: string;
}


// ── Execution / Jobs types ────────────────────────────────────────────


export interface ExecutionRow {
  id: string;
  study_id: string | null;
  study_name: string | null;
  study_type: string | null;
  status: "queued" | "running" | "completed" | "failed" | string;
  error: string;
  executed_at: string | null;
  completed_at: string | null;
  metrics: Record<string, any>;
}
